// Module 1 — Identité en tête de la fiche salarié (refonte visuelle à la manière d'Odoo RH).
// Ce qu'il faut comprendre en ouvrant un dossier : photo, nom, poste ou métier, service, site,
// matricule, statut et état du dossier ; puis les repères (responsable, ancienneté, contrat, équipe).
// Les actions vivent dans le bandeau de commande de la page (menu « Actions »).
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Hash, Phone, UserRound, CalendarClock, FileText, Users } from 'lucide-react';
import PhotoProfil from '../PhotoProfil';
import { Avatar, Badge, BadgeStatut, BadgeDossier, formaterDate, NON_RENSEIGNE } from '../../design/composants';

const nom = (p) => `${p?.prenom || ''} ${(p?.nom || '').toUpperCase()}`.trim();

function Repere({ Icone, libelle, children }) {
  return (
    <div className="rh-champ">
      <div className="rh-champ-libelle"><Icone size={13} aria-hidden="true" />{libelle}</div>
      <div className="rh-champ-valeur">{children}</div>
    </div>
  );
}

export default function BandeauSalarie({ synthese, photoModifiable, onPhotoMaj, onVoirDossier }) {
  const navigate = useNavigate();
  const s = synthese.salarie;
  const q = synthese.qualite;
  const r = synthese.responsable;
  const contrat = !s.type_contrat ? null
    : s.type_contrat === 'CDD' ? `CDD${s.date_fin_contrat ? ` jusqu'au ${formaterDate(s.date_fin_contrat)}` : ', date de fin non renseignée'}`
      : s.type_contrat;
  const service = [s.service, s.departement].filter(Boolean).join(' · ');
  const intitule = synthese.poste && synthese.metier ? `${synthese.metier.libelle} · ${synthese.poste}` : synthese.intitule?.libelle;

  return (
    <section className="rh-fiche-identite-bloc" aria-label="Identité du salarié">
      <div className="rh-fiche-bandeau">
        {photoModifiable
          ? <span className="rh-photo-fiche"><PhotoProfil employe={s} taille={96} editable clair onPhotoMaj={onPhotoMaj} /></span>
          : <Avatar chemin={s.photo_url} prenom={s.prenom} nom={s.nom} taille={96} className="rh-avatar-fiche" />}
        <div className="rh-fiche-identite">
          <h1 className="rh-fiche-nom">{nom(s)}</h1>
          <div className="rh-fiche-intitule" style={{ color: intitule ? undefined : 'var(--sg-texte-4)' }}>
            {intitule || 'Métier ou poste non renseigné'}
            {synthese.intitule?.source === 'libelle' && <span style={{ color: 'var(--sg-texte-4)' }}> (libellé non rattaché à un métier)</span>}
          </div>
          <ul className="rh-fiche-coordonnees">
            <li><Building2 size={15} aria-hidden="true" /><span style={{ color: service ? undefined : 'var(--sg-texte-4)' }}>{service || 'Service non renseigné'}</span></li>
            <li><MapPin size={15} aria-hidden="true" /><span style={{ color: s.site ? undefined : 'var(--sg-texte-4)' }}>{s.site ? s.site.nom : 'Site non renseigné'}</span></li>
            <li><Hash size={15} aria-hidden="true" /><span>Matricule {s.matricule}</span></li>
            {s.telephone_professionnel && <li><Phone size={15} aria-hidden="true" /><span>{s.telephone_professionnel}</span></li>}
          </ul>
        </div>
        <div className="rh-fiche-etats">
          {synthese.sortie ? <Badge ton="neutre" point>Sorti le {formaterDate(synthese.sortie.date)}</Badge> : <BadgeStatut statut={s.statut} />}
          {q && (
            <button type="button" className="rh-lien" onClick={onVoirDossier} title="Voir l'état du dossier" style={{ display: 'inline-flex' }}>
              <BadgeDossier etat={q.etat} nbBloquant={q.bloquants.length} nbACompleter={q.a_completer.length} />
            </button>
          )}
        </div>
      </div>

      <div className="rh-fiche-reperes">
        <Repere Icone={UserRound} libelle="Responsable">
          {r && r.etat === 'defini' ? (
            <button type="button" className="rh-lien" onClick={() => navigate(`/employes/${r.id}`)} title="Ouvrir la fiche du responsable">{nom(r)}</button>
          ) : r && r.etat === 'vacant' ? (
            <span style={{ color: 'var(--sg-alerte)' }}>Poste du responsable vacant</span>
          ) : <span style={{ color: 'var(--sg-alerte)' }}>Responsable à définir</span>}
          {r && ['defini', 'vacant'].includes(r.etat) && r.intitule && <div className="rh-cellule-secondaire">{r.intitule}</div>}
        </Repere>
        <Repere Icone={CalendarClock} libelle="Ancienneté">
          {synthese.anciennete ? synthese.anciennete.texte : <span className="rh-champ-vide">{NON_RENSEIGNE}</span>}
          {s.date_embauche && <div className="rh-cellule-secondaire">Entrée le {formaterDate(s.date_embauche)}{synthese.sortie ? `, sortie le ${formaterDate(synthese.sortie.date)}` : ''}</div>}
        </Repere>
        <Repere Icone={FileText} libelle="Contrat">
          {synthese.sortie ? <span className="rh-champ-vide">Clos à la sortie</span> : (contrat || <span className="rh-champ-vide">{NON_RENSEIGNE}</span>)}
        </Repere>
        <Repere Icone={Users} libelle="Équipe directe">
          {synthese.equipe_directe > 0 ? `${synthese.equipe_directe} personne${synthese.equipe_directe > 1 ? 's' : ''}` : <span className="rh-champ-vide">Aucune</span>}
        </Repere>
      </div>
    </section>
  );
}
